import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { initFilestackClient } from "network/filestack";
import { useSelector } from "react-redux";
import { userSelector } from "../../../../store/user/selector";
import { messagesEntryByChatIdSelector } from "../../../../store/support/selector";
import { useSupportChats, useSupportMessages } from "./index";
import { useSupportUnreadContext } from "../../../../contexts/SupportUnreadContext";

type Id = string | number;

const CHATS_PAGE_LIMIT = 6;
const MESSAGES_PAGE_LIMIT = 20;
const ACTIVE_CHAT_POLL_INTERVAL_MS = 5000;

export type SupportAttachment = {
  id: string;
  fileName: string;
  fileUrl: string;
};

export type SupportChatListItem = {
  id: Id;
  title: string;
  isClosed: boolean;
  displayDate: string;
  unreadCount: number;
};

type LocalMessageStatus = "sending" | "sent" | "failed";

type LocalSupportMessage = {
  client_id: string;
  body: string;
  created_at: string;
  sender_id?: string;
  attachments: string[];
  __local: {
    status: LocalMessageStatus;
  };
};

const toDisplayDate = (dateValue: unknown): string => {
  if (!(typeof dateValue === "string" || typeof dateValue === "number")) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  if (d >= startOfToday) return "Today";
  if (d >= startOfYesterday) return "Yesterday";
  return d.toLocaleDateString();
};

const isChatClosed = (rawStatus: unknown): boolean => {
  return (
    rawStatus === "closed" ||
    rawStatus === "CLOSED" ||
    rawStatus === 0 ||
    rawStatus === false
  );
};

type UseMessagesTabOptions = {
  autoSelectFirstChat?: boolean;
  forceFetchOnChatChange?: boolean;
};

export const useMessagesTab = (options?: UseMessagesTabOptions) => {
  const autoSelectFirstChat = options?.autoSelectFirstChat !== false;
  const forceFetchOnChatChange = options?.forceFetchOnChatChange === true;
  const user: any = useSelector(userSelector as any);
  const { refetch: refetchUnreadCount } = useSupportUnreadContext();

  const currentCustomerId =
    user?.user_id != null ? String(user.user_id) : undefined;

  const {
    chats,
    meta: chatsMeta,
    fetchChats,
    closeChat,
    isLoading: isChatsLoading,
  } = useSupportChats();
  const {
    fetchMessages,
    sendMessage,
    markRead,
  } = useSupportMessages();

  const inFlightChatIdRef = useRef<string | null>(null);
  const pollInFlightRef = useRef(false);

  const [chatsPage, setChatsPage] = useState(1);
  const [selectedChatId, setSelectedChatId] = useState<Id | null>(null);
  const [readChatIds, setReadChatIds] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<SupportAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const sendLockRef = useRef(false);
  const [scrollToBottomKey, setScrollToBottomKey] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isLoadingOlderByChatId, setIsLoadingOlderByChatId] = useState<Record<string, boolean>>({});
  const [hasMoreOlderByChatId, setHasMoreOlderByChatId] = useState<Record<string, boolean>>({});
  const [loadedPagesByChatId, setLoadedPagesByChatId] = useState<Record<string, number[]>>({});
  const [localMessagesByChatId, setLocalMessagesByChatId] = useState<
    Record<string, LocalSupportMessage[]>
  >({});

  const messagesEntrySelector = useMemo(
    () => messagesEntryByChatIdSelector(selectedChatId),
    [selectedChatId]
  );
  const messagesEntry = useSelector(messagesEntrySelector as any) as any;
  const serverMessages = messagesEntry?.messages ?? [];

  const messages = useMemo(() => {
    const safeServer = Array.isArray(messagesEntry?.messages)
      ? (messagesEntry?.messages ?? [])
      : [];

    const safeLocal =
      selectedChatId != null ? localMessagesByChatId[String(selectedChatId)] ?? [] : [];

    const toNum = (v: any) => {
      const n = typeof v === "number" ? v : Number(String(v));
      return Number.isFinite(n) ? n : null;
    };
    const toMessageId = (m: any) => {
      return toNum(m?.message_id ?? m?.id);
    };

    const normalizeBody = (v: any) => String(v ?? "").trim();
    const normalizeAttachments = (atts: any) => {
      const arr = Array.isArray(atts) ? atts : [];
      return arr.map((x) => String(x ?? "").trim()).filter(Boolean).sort();
    };

    const serverKeySet = new Set(
      safeServer.map((m: any) => {
        const sender = String(m?.sender_id ?? m?.sender ?? "");
        const body = normalizeBody(m?.body ?? m?.message);
        const attachments = normalizeAttachments(m?.attachments).join("|");
        return `${sender}::${body}::${attachments}`;
      })
    );

    const filteredLocal = safeLocal.filter((m: any) => {
      if (m?.__local?.status === "failed") return true;
      const sender = String(m?.sender_id ?? "");
      const body = normalizeBody(m?.body);
      const attachments = normalizeAttachments(m?.attachments).join("|");
      const key = `${sender}::${body}::${attachments}`;
      // If server already has this message, keep local hidden to avoid flicker/duplication.
      return !serverKeySet.has(key);
    });

    // Server messages sorted by message_id (ascending) so newest id is at the bottom.
    const sortedServer = safeServer
      .slice()
      .sort((a: any, b: any) => {
        const ai = toMessageId(a);
        const bi = toMessageId(b);
        if (ai == null && bi == null) return 0;
        if (ai == null) return -1;
        if (bi == null) return 1;
        return ai - bi;
      });

    // Local messages are always shown after server messages.
    return [...sortedServer, ...filteredLocal];
  }, [localMessagesByChatId, messagesEntry?.messages, selectedChatId]);

  const serverCount = Array.isArray(serverMessages) ? serverMessages.length : 0;
  const prevServerCountRef = useRef(serverCount);

  useEffect(() => {
    const prev = prevServerCountRef.current;
    if (serverCount > prev && isAtBottom) {
      setScrollToBottomKey((k) => k + 1);
    }
    prevServerCountRef.current = serverCount;
  }, [isAtBottom, serverCount]);

  useEffect(() => {
    fetchChats({ page: chatsPage, per_page: CHATS_PAGE_LIMIT });
  }, [chatsPage, fetchChats]);

  useEffect(() => {
    if (!autoSelectFirstChat) return;
    const nextChats = chats ?? [];
    if (nextChats.length === 0) return;

    const hasSelected =
      selectedChatId != null &&
      nextChats.some((c: any) => String(c?.chat_id ?? "") === String(selectedChatId));
    if (hasSelected) return;

    const firstId = nextChats[0]?.chat_id;
    if (firstId != null) setSelectedChatId(firstId);
  }, [autoSelectFirstChat, chats, selectedChatId]);

  const chatsPageRef = useRef(chatsPage);
  chatsPageRef.current = chatsPage;
  const fetchChatsRef = useRef(fetchChats);
  fetchChatsRef.current = fetchChats;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  useEffect(() => {
    if (selectedChatId == null) return;
    const key = String(selectedChatId);
    const chat = (chatsRef.current ?? []).find(
      (c: any) => String(c?.chat_id) === key
    );
    const unread = Number(chat?.unread_count) || 0;

    console.log("useMessagesTab markRead check:", {
      selectedChatId,
      chat,
      unread,
      chatId: chat?.chat_id,
      unreadCount: chat?.unread_count
    });

    let cancelled = false;
    const applyReadLocally = () => {
      setReadChatIds((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    };

    // If there are unread messages, keep the badge visible briefly so the user
    // can notice it before it disappears.
    const t = window.setTimeout(() => {
      if (cancelled) return;
      applyReadLocally();
    }, unread > 0 ? 2000 : 0);

    // Always try to mark as read when opening a chat, regardless of unread count
    // This ensures admin/manager chats are marked as read even if unread_count is not properly set
    markRead(selectedChatId, { silent: true })
      .then(() => {
        console.log("markRead successful for chat:", selectedChatId);
        // Refresh unread count after successfully marking messages as read
        refetchUnreadCount();
      })
      .catch((error) => {
        console.error("markRead failed for chat:", selectedChatId, error);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [selectedChatId, markRead, refetchUnreadCount]);

  useEffect(() => {
    if (selectedChatId == null) return;
    const cached = messagesEntry?.messages;
    if (!forceFetchOnChatChange) {
      if (Array.isArray(cached) && cached.length > 0) return;
    }
    if (messagesEntry?.isLoading) return;

    const key = String(selectedChatId);
    if (inFlightChatIdRef.current === key) return;
    inFlightChatIdRef.current = key;

    fetchMessages(selectedChatId, {
      force: forceFetchOnChatChange,
      page: 1,
      per_page: MESSAGES_PAGE_LIMIT,
      mode: "replace",
    }).finally(() => {
      if (inFlightChatIdRef.current === key) inFlightChatIdRef.current = null;
    });

    setHasMoreOlderByChatId((prev) => ({ ...prev, [key]: true }));
    setLoadedPagesByChatId((prev) => ({ ...prev, [key]: [1] }));
  }, [fetchMessages, forceFetchOnChatChange, messagesEntry?.isLoading, messagesEntry?.messages, selectedChatId]);

  useEffect(() => {
    if (selectedChatId == null) return;

    const chatId = selectedChatId;
    const key = String(chatId);

    const pollActiveChat = async () => {
      if (document.visibilityState === "hidden") return;
      if (pollInFlightRef.current || inFlightChatIdRef.current === key) return;

      pollInFlightRef.current = true;
      try {
        const loadedServerCount = serverCount || MESSAGES_PAGE_LIMIT;
        const perPage = Math.max(MESSAGES_PAGE_LIMIT, loadedServerCount);

        await fetchMessages(chatId, {
          page: 1,
          per_page: perPage,
          mode: "replace",
          silent: true,
        });
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(
      pollActiveChat,
      ACTIVE_CHAT_POLL_INTERVAL_MS
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchMessages, selectedChatId, serverCount]);

  const openFilePicker = useCallback(async () => {
    if (isUploading) return;
    setIsUploading(true);

    const filestack = await initFilestackClient();
    filestack
      .picker({
        maxFiles: 10,
        maxSize: 25 * 1024 * 1024,
        accept: [
          "image/*",
          ".pdf",
          ".doc",
          ".docx",
          ".xls",
          ".xlsx",
          ".txt",
          ".csv",
          ".json",
        ],
        onUploadDone: (result: any) => {
          const uploaded = result?.filesUploaded ?? [];
          if (Array.isArray(uploaded) && uploaded.length > 0) {
            const newFiles = uploaded.map((file: any) => ({
              id: String(file.handle),
              fileName: String(file.filename ?? file.handle ?? "file"),
              fileUrl: String(file.url ?? ""),
            }));
            setAttachedFiles((prev) =>
              [...prev, ...newFiles].filter((f) => !!f?.fileUrl)
            );
          }
          setIsUploading(false);
        },
        onClose: () => setIsUploading(false),
      })
      .open();
  }, [isUploading]);

  const removeFile = useCallback((fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const onSend = useCallback(async () => {
    if (selectedChatId == null) return;
    if (sendLockRef.current) return;
    if (!draft.trim() && attachedFiles.length === 0) return;
    sendLockRef.current = true;

    const chatKey = String(selectedChatId);
    const attachments = attachedFiles.map((f) => f.fileUrl).filter(Boolean);
    const body = draft.trim();

    const clientId = `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimisticMessage: LocalSupportMessage = {
      client_id: clientId,
      body,
      attachments,
      created_at: new Date().toLocaleString(),
      sender_id: currentCustomerId,
      __local: { status: "sending" },
    };

    setLocalMessagesByChatId((prev) => ({
      ...prev,
      [chatKey]: [...(prev[chatKey] ?? []), optimisticMessage],
    }));

    setScrollToBottomKey((k) => k + 1);

    setDraft("");
    setAttachedFiles([]);

    setIsSending(true);
    try {
      await sendMessage(selectedChatId, {
        body,
        attachments,
      });
      setLocalMessagesByChatId((prev) => {
        const next = { ...prev };
        next[chatKey] = (next[chatKey] ?? []).map((m) =>
          m.client_id === clientId ? { ...m, __local: { status: "sent" } } : m
        );
        return next;
      });
      const loadedServerCount = serverCount || MESSAGES_PAGE_LIMIT;
      const perPage = Math.max(MESSAGES_PAGE_LIMIT, loadedServerCount);

      await fetchMessages(selectedChatId, {
        force: true,
        silent: true,
        page: 1,
        per_page: perPage,
        mode: "replace",
      });
    } catch {
      setLocalMessagesByChatId((prev) => {
        const next = { ...prev };
        next[chatKey] = (next[chatKey] ?? []).map((m) =>
          m.client_id === clientId ? { ...m, __local: { status: "failed" } } : m
        );
        return next;
      });
    } finally {
      sendLockRef.current = false;
      setIsSending(false);
    }
  }, [attachedFiles, currentCustomerId, draft, fetchMessages, selectedChatId, sendMessage, serverCount]);

  const isLoadingOlder =
    selectedChatId != null ? Boolean(isLoadingOlderByChatId[String(selectedChatId)]) : false;

  const hasMoreOlder =
    selectedChatId != null ? (hasMoreOlderByChatId[String(selectedChatId)] ?? true) : true;

  const onLoadOlder = useCallback(async () => {
    if (selectedChatId == null) return;
    const chatKey = String(selectedChatId);
    if (isLoadingOlderByChatId[chatKey]) return;
    if (hasMoreOlderByChatId[chatKey] === false) return;

    const entryMeta = messagesEntry?.meta ?? null;
    const metaPage = typeof entryMeta?.page === "number" ? entryMeta.page : Number(entryMeta?.page);
    const metaLast =
      typeof entryMeta?.lastPage === "number"
        ? entryMeta.lastPage
        : typeof entryMeta?.last_page === "number"
          ? entryMeta.last_page
          : Number(entryMeta?.lastPage ?? entryMeta?.last_page);

    if (Number.isFinite(metaPage) && Number.isFinite(metaLast) && metaPage >= metaLast) {
      setHasMoreOlderByChatId((prev) => ({ ...prev, [chatKey]: false }));
      return;
    }

    const loadedPages = loadedPagesByChatId[chatKey] ?? [1];
    const maxLoadedPage = loadedPages.length > 0 ? Math.max(...loadedPages) : 1;
    const nextPage = maxLoadedPage + 1;

    if (Number.isFinite(metaLast) && nextPage > metaLast) {
      setHasMoreOlderByChatId((prev) => ({ ...prev, [chatKey]: false }));
      return;
    }
    if (loadedPages.includes(nextPage)) return;

    setIsLoadingOlderByChatId((prev) => ({ ...prev, [chatKey]: true }));
    try {
      const older = await fetchMessages(selectedChatId, {
        page: nextPage,
        per_page: MESSAGES_PAGE_LIMIT,
        mode: "prepend",
        silent: true,
      });
      const olderCount = Array.isArray(older) ? older.length : 0;
      // Mark page as attempted/loaded to avoid repeated requests when user scrolls.
      setLoadedPagesByChatId((prev) => ({
        ...prev,
        [chatKey]: [...(prev[chatKey] ?? [1]), nextPage],
      }));

      if (Number.isFinite(metaLast) && nextPage >= metaLast) {
        setHasMoreOlderByChatId((prev) => ({ ...prev, [chatKey]: false }));
      } else if (olderCount < MESSAGES_PAGE_LIMIT) {
        // Fallback if meta отсутствует или неконсистентна
        setHasMoreOlderByChatId((prev) => ({ ...prev, [chatKey]: false }));
      }
    } finally {
      setIsLoadingOlderByChatId((prev) => ({ ...prev, [chatKey]: false }));
    }
  }, [fetchMessages, hasMoreOlderByChatId, isLoadingOlderByChatId, loadedPagesByChatId, messagesEntry?.meta, selectedChatId]);

  const onRetrySend = useCallback(async (clientId: string) => {
    if (selectedChatId == null) return;
    const chatKey = String(selectedChatId);
    const list = localMessagesByChatId[chatKey] ?? [];
    const msg = list.find((m) => m.client_id === clientId);
    if (!msg) return;

    setLocalMessagesByChatId((prev) => {
      const next = { ...prev };
      next[chatKey] = (next[chatKey] ?? []).map((m) =>
        m.client_id === clientId ? { ...m, __local: { status: "sending" } } : m
      );
      return next;
    });

    try {
      await sendMessage(selectedChatId, {
        body: msg.body,
        attachments: msg.attachments,
      });
      setLocalMessagesByChatId((prev) => {
        const next = { ...prev };
        next[chatKey] = (next[chatKey] ?? []).map((m) =>
          m.client_id === clientId ? { ...m, __local: { status: "sent" } } : m
        );
        return next;
      });
      const loadedServerCount = serverCount || MESSAGES_PAGE_LIMIT;
      const perPage = Math.max(MESSAGES_PAGE_LIMIT, loadedServerCount);

      await fetchMessages(selectedChatId, {
        force: true,
        silent: true,
        page: 1,
        per_page: perPage,
        mode: "replace",
      });
    } catch {
      setLocalMessagesByChatId((prev) => {
        const next = { ...prev };
        next[chatKey] = (next[chatKey] ?? []).map((m) =>
          m.client_id === clientId ? { ...m, __local: { status: "failed" } } : m
        );
        return next;
      });
    }
  }, [fetchMessages, localMessagesByChatId, selectedChatId, sendMessage, serverCount]);

  const chatItems: SupportChatListItem[] = useMemo(() => {
    return (chats ?? [])
      .map((chat: any) => {
        const id = chat?.chat_id;
        const title =
          chat?.topic ?? chat?.title ?? chat?.subject ?? "Support request";
        const rawStatus =
          chat?.status ?? chat?.state ?? (chat?.is_closed ? "closed" : "open");
        const dateValue =
          chat?.updated_at ?? chat?.last_message_at ?? chat?.created_at;

        const rawUnread = Number(chat?.unread_count) || 0;
        const unreadCount = readChatIds.has(String(id)) ? 0 : rawUnread;

        return {
          id,
          title,
          isClosed: isChatClosed(rawStatus),
          displayDate: toDisplayDate(dateValue),
          unreadCount,
        };
      })
      .filter((it: any) => it?.id != null);
  }, [chats, readChatIds]);

  const selectedChat = useMemo(() => {
    if (selectedChatId == null) return null;
    return (chats ?? []).find((c: any) => String(c?.chat_id) === String(selectedChatId)) ?? null;
  }, [chats, selectedChatId]);

  const selectedChatTitle = useMemo(() => {
    return (
      selectedChat?.topic ??
      selectedChat?.title ??
      selectedChat?.subject ??
      "Support request"
    );
  }, [selectedChat]);

  const selectedChatDisplayDate = useMemo(() => {
    const dateValue =
      selectedChat?.updated_at ??
      selectedChat?.last_message_at ??
      selectedChat?.created_at;
    return toDisplayDate(dateValue);
  }, [selectedChat]);

  const selectedChatStatus = useMemo(() => {
    if (selectedChat == null) return "";
    const rawStatus =
      selectedChat?.status ??
      selectedChat?.state ??
      (selectedChat?.is_closed ? "closed" : "open");
    return isChatClosed(rawStatus) ? "Closed" : "Open";
  }, [selectedChat]);

  return {
    chats,
    chatItems,
    isChatsLoading,

    closeChat,

    chatsMeta,
    chatsPage,
    setChatsPage,

    selectedChatId,
    setSelectedChatId,

    selectedChat,
    selectedChatTitle,
    selectedChatDisplayDate,
    selectedChatStatus,

    messages,
    isMessagesLoading:
      Boolean(messagesEntry?.isLoading) &&
      (!Array.isArray(messagesEntry?.messages) || messagesEntry.messages.length === 0),

    onRetrySend,

    scrollToBottomKey,
    setIsAtBottom,
    onLoadOlder,
    isLoadingOlder,
    hasMoreOlder,

    currentCustomerId,

    draft,
    setDraft,

    attachedFiles,
    isUploading,
    openFilePicker,
    removeFile,

    isSending,
    onSend,
  };
};
